import { toast, useLocalization, createMutation } from '@magicdoorinc/ui';
import { createResource, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { AttachmentsRepository } from '~/repositories/attachmentsRepository';
import type { EntityType, MagicUserType } from '~/swagger/portal/Api';

const repo = new AttachmentsRepository();

class UserCancelledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserCancelledError';
  }
}

type AttachmentsFilter = {
  entityType: `${EntityType}`;
  entityId: string;
};

export const [AttachmentsProvider, useAttachments] = createMagicDoorContext('Attachments', () => {
  const [filter, setFilter] = createSignal<AttachmentsFilter>();
  const abortControllers = new Set<AbortController>();
  const abortMap = new WeakMap<File, AbortController>();
  let isCancelled = false;
  const { t } = useLocalization();
  const [attachments, { refetch, mutate }] = createResource(filter, async ({ entityType, entityId }) =>
    repo.getAttachments(entityType, entityId)
  );

  const addAttachments = createMutation(
    async (
      entityType: `${EntityType}`,
      entityId: string,
      items: { file: File; description?: string; shareTypes?: `${MagicUserType}`[] }[]
    ) => {
      if (isCancelled) {
        throw new UserCancelledError('User cancelled the upload');
      }
      const abortController = new AbortController();
      abortControllers.add(abortController);
      try {
        const created = await repo.addAttachments(entityType, entityId, items, abortController.signal);
        mutate((prev) => prev && [...created, ...prev]);
      } catch (error) {
        if (error instanceof UserCancelledError) {
          toast.error(t(error.message));
          throw error;
        }
        throw error;
      } finally {
        abortControllers.delete(abortController);
      }
    }
  );

  const addAttachment = createMutation(
    async (entityType: `${EntityType}`, entityId: string, file: File, description?: string, shareTypes?: `${MagicUserType}`[]) => {
      if (isCancelled) {
        throw new UserCancelledError('User cancelled the upload');
      }
      const abortController = new AbortController();
      abortControllers.add(abortController);
      abortMap.set(file, abortController);
      try {
        const created = await repo.addAttachments(entityType, entityId, [{ file, description, shareTypes }], abortController.signal);
        mutate((prev) => prev && [...created, ...prev]);
        return created[0];
      } catch (error) {
        if (error instanceof UserCancelledError) {
          toast.error(t(error.message));
          throw error;
        }
        throw error;
      } finally {
        abortControllers.delete(abortController);
        abortMap.delete(file);
      }
    }
  );

  const createAttachmentFromTemplate = createMutation(
    async (entityType: `${EntityType}`, entityId: string, payload: { letterDocumentTemplateId: string; fileName?: string }) => {
      const created = await repo.createAttachmentFromTemplate(entityType, entityId, payload);
      mutate((prev) => prev && [created, ...prev]);
      return created;
    }
  );

  const updateAttachment = createMutation(
    async (
      entityType: `${EntityType}`,
      entityId: string,
      fileAttachmentId: string,
      updateData: { fileName: string; description?: string }
    ) => {
      const updated = await repo.updateAttachment(entityType, entityId, fileAttachmentId, updateData);
      mutate((prev) => prev?.map((attachment) => (attachment.id === fileAttachmentId ? { ...attachment, ...updated } : attachment)));
    }
  );

  const deleteAttachment = createMutation(async (entityType: `${EntityType}`, entityId: string, fileAttachmentId: string) => {
    await repo.deleteAttachment(entityType, entityId, fileAttachmentId);
    mutate((prev) => prev && prev.filter((attachment) => attachment.id !== fileAttachmentId));
  });

  const cancelAttachment = () => {
    isCancelled = true;
    abortControllers.forEach((controller) => controller.abort(new UserCancelledError('User cancelled the upload')));
    abortControllers.clear();
  };

  const cancelAttachmentByFile = (file: File): boolean => {
    const controller = abortMap.get(file);
    if (controller) {
      controller.abort(new UserCancelledError('User cancelled the upload'));
      abortControllers.delete(controller);
      abortMap.delete(file);
      return true;
    }
    return false;
  };

  const resetCancellation = () => {
    isCancelled = false;
  };

  const setFileShareTypes = createMutation(
    async (entityType: `${EntityType}`, entityId: string, fileAttachmentId: string, payload: { shareTypes: `${MagicUserType}`[] }) => {
      const updated = await repo.setFileShareTypes(entityType, entityId, fileAttachmentId, payload);
      mutate((prev) => prev?.map((attachment) => (attachment.id === fileAttachmentId ? { ...attachment, ...updated } : attachment)));
    }
  );

  return {
    attachments,
    refetch,
    getAttachments: (entityType: `${EntityType}`, entityId: string) => setFilter({ entityType, entityId }),
    addAttachment,
    addAttachments,
    createAttachmentFromTemplate,
    deleteAttachment,
    cancelAttachment,
    cancelAttachmentByFile,
    resetCancellation,
    updateAttachment,
    setFileShareTypes,
  };
});
